import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, Alert, CircularProgress, Chip,
} from '@mui/material';
import {
  Close, Person, Work, School, FamilyRestroom, Favorite, Restaurant,
  Diversity3, ContactPhone, TempleHindu, HealthAndSafety, Home,
  LocationOn, PhotoLibrary, Verified, WarningAmber, Edit, CalendarMonth,
} from '@mui/icons-material';
import adminService from '../../services/adminService';
import './ViewProfileDialog.css';

// Values that read as "not really filled in" even though the field has a
// string in it (registration defaults like "Not Specified"/"None").
const hiddenValues = new Set(['not specified', 'n/a', 'na', 'none', 'no', 'false', 'undefined', 'null', '']);

const hasValue = (value, { showNo = false, showNone = false } = {}) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.some((v) => hasValue(v, { showNo, showNone }));
  if (typeof value === 'number') return !Number.isNaN(value);
  const text = String(value).trim();
  if (!text) return false;
  const normalized = text.toLowerCase();
  if (showNo && normalized === 'no') return true;
  if (showNone && normalized === 'none') return true;
  return !hiddenValues.has(normalized);
};

const displayText = (value, options) => {
  if (Array.isArray(value)) return value.filter((v) => hasValue(v, options)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : '';
  return hasValue(value, options) ? String(value).trim() : '';
};

// Unlike the member-facing page (which hides unfilled fields entirely), the
// admin view always renders every tile so gaps are visible — a blank tile
// is exactly what the team is here to find.
const addItem = (items, label, value, options) => {
  items.push({ label, value: displayText(value, options) });
};

const calcAge = (dob) => {
  if (!dob) return null;
  const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  return Number.isFinite(age) ? age : null;
};

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatHeight = (cm) => {
  const numeric = Number(cm);
  if (!numeric) return '';
  const inches = Math.round(numeric / 2.54);
  const feet = Math.floor(inches / 12);
  const remaining = inches % 12;
  return `${feet}'${remaining}" (${numeric} cm)`;
};

const formatIncome = (income) => {
  if (!income || !hasValue(income.amount)) return '';
  return `${income.currency || 'INR'} ${income.amount} ${income.period || 'Yearly'}`;
};

const formatLocation = (loc = {}) => [loc.city, loc.state, loc.country].filter((v) => hasValue(v)).join(', ');

const getProfilePhoto = (profile) => {
  const photos = profile?.photos || [];
  return photos.find((p) => p?.isProfilePic)?.url || photos[0]?.url || '';
};

const getCareerDisplay = (profile) => {
  const career = profile.career || {};
  return {
    employedIn: career.employedIn,
    occupation: career.occupation || profile.occupation,
    organization: career.organizationName || profile.employer,
    settlingAbroad: career.interestedInSettlingAbroad,
    notes: career.thoughtsOnSettlingAbroad,
    profession: career.occupation || profile.occupation,
  };
};

const Section = ({ title, icon, children }) => (
  <section className="admin-matrimony-section">
    <div className="admin-matrimony-section-title">
      <span className="admin-matrimony-section-icon">{icon}</span>
      <h2>{title}</h2>
    </div>
    {children}
  </section>
);

const DetailGrid = ({ items }) => (
  <div className="admin-details-grid">
    {items.map((item) => (
      <div className={`admin-detail-tile${item.value ? '' : ' missing'}`} key={item.label}>
        <span>{item.label}</span>
        <strong>{item.value || 'Not filled'}</strong>
      </div>
    ))}
  </div>
);

const ChipList = ({ values }) => {
  const visible = (values || []).filter((v) => hasValue(v));
  if (!visible.length) return null;
  return (
    <div className="admin-profile-chip-list">
      {visible.map((v) => <span key={v}>{v}</span>)}
    </div>
  );
};

const TextBlock = ({ children }) => hasValue(children) ? <p className="admin-profile-text-block">{children}</p> : null;

const completionTone = (pct) => (pct >= 80 ? '' : pct >= 50 ? 'mid' : 'low');

const ViewProfileDialog = ({ open, userId, onClose, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (open && userId) loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const loadDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminService.getUserDetails(userId);
      setUser(res.data.data.user);
      setProfile(res.data.data.profile || null);
    } catch {
      setError('Failed to load profile details');
    } finally {
      setLoading(false);
    }
  };

  const completionPercentage = profile?.profileCompletion?.percentage ?? 0;

  const checklist = profile ? [
    { label: 'Photo', filled: !!profile.photos?.length },
    { label: 'About Me', filled: hasValue(profile.aboutMe) },
    { label: 'Partner Requirement', filled: hasValue(profile.partnerPreferences?.aboutPartner) },
    { label: 'Education', filled: hasValue(profile.education?.highestDegree) },
    { label: 'Occupation', filled: hasValue(profile.career?.occupation || profile.occupation) },
    { label: 'Marital Status', filled: hasValue(profile.maritalStatus) },
    { label: 'Family / Siblings', filled: profile.familyDetails?.brothers?.total != null || profile.familyDetails?.sisters?.total != null },
    { label: 'Religion', filled: hasValue(profile.religion) },
    { label: 'Caste / Community', filled: hasValue(profile.community || profile.caste) },
    { label: 'State', filled: hasValue(profile.locationDetails?.state) },
    { label: 'City', filled: hasValue(profile.locationDetails?.city) },
  ] : [];
  const missing = checklist.filter((c) => !c.filled);

  let view = null;
  if (profile) {
    const age = calcAge(profile.dateOfBirth);
    const location = formatLocation(profile.locationDetails);
    const career = getCareerDisplay(profile);

    const personalItems = [];
    addItem(personalItems, 'User ID', user?.userId);
    addItem(personalItems, 'Age', age ? `${age} years` : '');
    addItem(personalItems, 'Date of Birth', formatDate(profile.dateOfBirth));
    addItem(personalItems, 'Gender', user?.gender || profile.gender);
    addItem(personalItems, 'Height', formatHeight(profile.height));
    addItem(personalItems, 'Weight', profile.weight ? `${profile.weight} kg` : '');
    addItem(personalItems, 'Marital Status', profile.maritalStatus);
    addItem(personalItems, 'Looking For', user?.lookingFor || 'Marriage');
    addItem(personalItems, 'Profile Managed By', profile.profileManagedBy);

    const healthItems = [];
    addItem(healthItems, 'Disability', profile.disability);
    addItem(healthItems, 'Thalassemia', profile.thalassemia);
    addItem(healthItems, 'HIV', profile.hivPositive ? 'Positive' : 'Negative');

    const religionItems = [];
    addItem(religionItems, 'Religion', profile.religion);
    addItem(religionItems, 'Caste / Community', profile.community || profile.caste);
    addItem(religionItems, 'Sub Community', profile.subCommunity);
    addItem(religionItems, 'Mother Tongue', profile.motherTongue);
    addItem(religionItems, 'Gothra', profile.gothra);

    const educationItems = [];
    addItem(educationItems, 'Highest Degree', profile.education?.highestDegree);
    addItem(educationItems, 'UG Degree', profile.education?.ugDegree);
    addItem(educationItems, 'College', profile.education?.ugCollege);
    addItem(educationItems, 'School', profile.education?.schoolName);

    const careerItems = [];
    addItem(careerItems, 'Employed In', career.employedIn);
    addItem(careerItems, 'Occupation', career.occupation);
    addItem(careerItems, 'Organization', career.organization);
    addItem(careerItems, 'Income', formatIncome(profile.income) || formatIncome(profile.annualIncome));
    addItem(careerItems, 'Settling Abroad', career.settlingAbroad);

    const family = profile.familyDetails || {};
    const familyItems = [];
    addItem(familyItems, 'Family Status', family.familyStatus);
    addItem(familyItems, 'Family Type', family.familyType);
    addItem(familyItems, 'Family Values', family.familyValues);
    addItem(familyItems, 'Living With Parents', family.livingWithParents, { showNo: true });
    addItem(familyItems, "Father's Occupation", family.fatherOccupation);
    addItem(familyItems, "Mother's Occupation", family.motherOccupation);
    addItem(familyItems, 'Brothers', hasValue(family.brothers?.total) ? `${family.brothers.total} total, ${family.brothers.married || 0} married` : '');
    addItem(familyItems, 'Sisters', hasValue(family.sisters?.total) ? `${family.sisters.total} total, ${family.sisters.married || 0} married` : '');

    const lifestyle = profile.lifestyle || {};
    const interests = lifestyle.interests || {};
    const lifestyleItems = [];
    addItem(lifestyleItems, 'Diet', lifestyle.diet);
    addItem(lifestyleItems, 'Drinking', lifestyle.drinking, { showNo: true });
    addItem(lifestyleItems, 'Smoking', lifestyle.smoking, { showNo: true });
    addItem(lifestyleItems, 'Hobbies', interests.hobbies);
    addItem(lifestyleItems, 'Languages Spoken', interests.languagesSpoken);

    const locationItems = [];
    addItem(locationItems, 'Country', profile.locationDetails?.country);
    addItem(locationItems, 'State', profile.locationDetails?.state);
    addItem(locationItems, 'City', profile.locationDetails?.city);
    addItem(locationItems, 'Citizenship', profile.locationDetails?.citizenshipStatus);
    addItem(locationItems, 'Current Location', profile.currentLocation);

    const kundli = profile.kundliDetails || {};
    const kundliItems = [];
    addItem(kundliItems, 'Manglik', kundli.manglikStatus, { showNo: true });
    addItem(kundliItems, 'Nakshatra', kundli.nakshatra);
    addItem(kundliItems, 'Time of Birth', kundli.timeOfBirth);
    addItem(kundliItems, 'Place of Birth', kundli.placeOfBirth);

    // Admin always sees raw contact info — no "reveal contact" gating.
    const contact = profile.contact || {};
    const contactItems = [];
    addItem(contactItems, 'Email', contact.email || user?.email);
    addItem(contactItems, 'Phone', contact.phoneNumber || user?.phone);
    addItem(contactItems, 'Alternate Email', contact.alternateEmail);
    addItem(contactItems, 'Alternate Number', contact.alternateNumber);
    addItem(contactItems, 'Email Verified', user?.isEmailVerified ? 'Yes' : 'No');
    addItem(contactItems, 'Phone Verified', contact.isPhoneVerified ? 'Yes' : 'No');

    const prefs = profile.partnerPreferences || {};
    const preferenceItems = [];
    addItem(preferenceItems, 'Age Range', (prefs.ageRange?.min || prefs.ageRange?.max) ? `${prefs.ageRange?.min || 'Any'} - ${prefs.ageRange?.max || 'Any'} yrs` : '');
    addItem(preferenceItems, 'Height Range', (prefs.heightRange?.min || prefs.heightRange?.max) ? `${formatHeight(prefs.heightRange?.min) || 'Any'} - ${formatHeight(prefs.heightRange?.max) || 'Any'}` : '');
    addItem(preferenceItems, 'Marital Status', prefs.maritalStatus);
    addItem(preferenceItems, 'Religion', prefs.religions);
    addItem(preferenceItems, 'Education', prefs.educationLevel);
    addItem(preferenceItems, 'Occupation', prefs.occupations);

    const assetItems = [];
    addItem(assetItems, 'Own House', lifestyle.assets?.ownHouse ? 'Yes' : 'No');
    addItem(assetItems, 'Own Car', lifestyle.assets?.ownCar ? 'Yes' : 'No');
    addItem(assetItems, 'Assets Description', lifestyle.assets?.description);

    view = {
      age, location, photo: getProfilePhoto(profile), photoCount: profile.photos?.length || 0,
      personalItems, healthItems, religionItems, educationItems, careerItems, familyItems,
      lifestyleItems, locationItems, kundliItems, contactItems, preferenceItems, assetItems,
      career, aboutEducation: profile.education?.aboutEducation, aboutFamily: family.aboutFamily,
      aboutPartner: prefs.aboutPartner,
    };
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="span">View Profile</Typography>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : !profile ? (
          <Alert severity="info" sx={{ m: 2 }}>This user has not created a profile yet.</Alert>
        ) : (
          <div className="admin-profile-view">
            <section className="admin-profile-hero">
              <div className="admin-profile-hero-media">
                {view.photo ? (
                  <img src={view.photo} alt={user?.name || 'Profile'} />
                ) : (
                  <div className="admin-profile-photo-placeholder"><Person /></div>
                )}
                {view.photoCount > 0 && (
                  <span className="admin-photo-count"><PhotoLibrary fontSize="small" /> {view.photoCount} photos</span>
                )}
              </div>

              <div className="admin-profile-hero-info">
                <div className="admin-hero-status-row">
                  {profile.profileCompletion?.isVerified && (
                    <span className="admin-verified-pill"><Verified fontSize="small" /> Verified</span>
                  )}
                  <span className={`admin-completion-pill ${completionTone(completionPercentage)}`}>
                    Profile {completionPercentage}% complete
                  </span>
                  {profile.lastActive && (
                    <span className="admin-muted-pill"><CalendarMonth fontSize="small" /> Active {formatDate(profile.lastActive)}</span>
                  )}
                  {user?.membershipType && <span className="admin-muted-pill">{user.membershipType}</span>}
                </div>

                <h1>{user?.name || 'Anonymous'}{view.age ? `, ${view.age}` : ''}</h1>

                <div className="admin-hero-subtitle">
                  {view.career.profession && hasValue(view.career.profession) && <span><Work fontSize="small" /> {view.career.profession}</span>}
                  {view.location && <span><LocationOn fontSize="small" /> {view.location}</span>}
                </div>

                <ChipList values={[profile.maritalStatus, profile.religion, profile.community || profile.caste, profile.motherTongue]} />
                <TextBlock>{profile.aboutMe}</TextBlock>

                {missing.length > 0 && (
                  <div className="admin-followup-card">
                    <span><WarningAmber fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />Needs follow-up — missing {missing.length} item(s)</span>
                    <div className="admin-followup-chips">
                      {missing.map((m) => <span key={m.label}>{m.label}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <div className="admin-profile-layout">
              <main className="admin-profile-main-column">
                <Section title="Personal Details" icon={<Person />}><DetailGrid items={view.personalItems} /></Section>
                <Section title="Health Information" icon={<HealthAndSafety />}><DetailGrid items={view.healthItems} /></Section>
                <Section title="Religion & Community" icon={<TempleHindu />}><DetailGrid items={view.religionItems} /></Section>
                <Section title="Education" icon={<School />}>
                  <DetailGrid items={view.educationItems} />
                  <TextBlock>{view.aboutEducation}</TextBlock>
                </Section>
                <Section title="Career" icon={<Work />}><DetailGrid items={view.careerItems} /></Section>
                <Section title="Family Details" icon={<FamilyRestroom />}>
                  <DetailGrid items={view.familyItems} />
                  <TextBlock>{view.aboutFamily}</TextBlock>
                </Section>
                <Section title="Lifestyle" icon={<Restaurant />}><DetailGrid items={view.lifestyleItems} /></Section>

                <Section title="Photos" icon={<PhotoLibrary />}>
                  {!profile.photos?.length ? (
                    <Typography variant="body2" color="text.secondary">No photos uploaded.</Typography>
                  ) : (
                    <div className="admin-photo-gallery">
                      {profile.photos.map((photo) => (
                        <div className={`admin-photo-gallery-item${photo.isProfilePic ? ' is-primary' : ''}`} key={photo._id || photo.url}>
                          <img src={photo.url} alt="profile" />
                          <Chip
                            size="small"
                            sx={{ mt: 0.5 }}
                            color={
                              photo.moderationStatus === 'approved' ? 'success'
                                : photo.moderationStatus === 'rejected' ? 'error'
                                : photo.moderationStatus === 'reupload_required' ? 'default'
                                : 'warning'
                            }
                            label={
                              photo.moderationStatus === 'approved' ? 'Approved'
                                : photo.moderationStatus === 'rejected' ? 'Rejected'
                                : photo.moderationStatus === 'reupload_required' ? 'Re-upload Required'
                                : 'Pending'
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </main>

              <aside className="admin-profile-side-column">
                <Section title="Location" icon={<LocationOn />}><DetailGrid items={view.locationItems} /></Section>
                <Section title="Kundli & Astro" icon={<Diversity3 />}><DetailGrid items={view.kundliItems} /></Section>
                <Section title="Contact" icon={<ContactPhone />}><DetailGrid items={view.contactItems} /></Section>
                <Section title="Partner Preferences" icon={<Favorite />}>
                  <DetailGrid items={view.preferenceItems} />
                  {hasValue(view.aboutPartner) && (
                    <Box sx={{ mt: 1.5 }}>
                      <TextBlock>{view.aboutPartner}</TextBlock>
                    </Box>
                  )}
                </Section>
                <Section title="Home & Assets" icon={<Home />}><DetailGrid items={view.assetItems} /></Section>
              </aside>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">Close</Button>
        <Button
          onClick={() => onEdit?.(userId)}
          variant="contained"
          startIcon={<Edit />}
          disabled={loading || !!error}
        >
          Edit Profile
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewProfileDialog;
